import React, { useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

export type ProgramOption = { id: string; name: string };

type ProgramComboboxProps = {
  options: ProgramOption[];
  onSelect: (programId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  keepOpenOnSelect?: boolean;
};

export const ProgramCombobox: React.FC<ProgramComboboxProps> = ({
  options,
  onSelect,
  placeholder = 'Начните вводить название программы...',
  disabled,
  keepOpenOnSelect = true,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 120);
  };

  const handleFocus = () => {
    if (!disabled) setOpen(true);
  };

  return (
    <div className="w-full">
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <SearchIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="programs-combobox-listbox"
          aria-autocomplete="list"
          placeholder={placeholder}
          disabled={disabled}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onClick={() => {
            if (!disabled) setOpen(true);
          }}
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-10 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
        />

        {open && (
          <button
            type="button"
            aria-label="Закрыть список программ"
            className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        )}

        {open && (
          <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
            {filtered.length > 0 ? (
              <ul id="programs-combobox-listbox" role="listbox" className="max-h-64 overflow-auto py-1">
                {filtered.map((opt) => (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected="false"
                    className="group flex cursor-pointer items-center justify-between px-3 py-2 text-sm text-gray-800 border-l-2 border-transparent transition-colors duration-150 hover:bg-gray-50 hover:border-blue-500"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      onSelect(opt.id);
                      setQuery('');
                      if (keepOpenOnSelect) {
                        inputRef.current?.focus();
                      } else {
                        setOpen(false);
                      }
                    }}
                  >
                    <span className="min-w-0 truncate">{opt.name}</span>
                    <span className="ml-3 flex h-5 w-5 items-center justify-center text-blue-500 transition-colors duration-150 group-hover:text-blue-600">
                      <AddIcon fontSize="small" />
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">Ничего не найдено</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};